import { VdsIcon, VdsIconButton } from '@volt/volt-extended-components-react';
import styles from './AssetTrackCard.module.scss';
import { ReactElement } from 'react';
import { useAppDispatch } from '@casops/store/hooks';
import { removeTrack, toggleTrackVisibility } from '@casops/store/slices/assetTracksSlice';
import { useLocaleFormatter } from '@casops/shared/hooks/useLocaleFormatter';
import classNames from 'classnames';
import { useAssetTrack } from './useAssetTrack';

interface AssetTrackCardProps {
  trackId: string;
}

export const AssetTrackCard = ({ trackId }: AssetTrackCardProps): ReactElement | null => {
  const dispatch = useAppDispatch();
  const { showDateOnlyFromIso, showTimeOnlyFromIso } = useLocaleFormatter();

  const { trackRecord, isTrackRecordValid, data, isSuccess, isError } = useAssetTrack({ trackId });

  const noTrackData = data && data.tracks.length === 0; // Data has returned, but it contains no tracks.
  const hasTrackDataLoaded = isSuccess && data && data.tracks.length > 0; // Data has returned with tracks.

  if (trackRecord && isTrackRecordValid) {
    const { assetName, startTime, endTime, visible } = trackRecord;
    const onToggleVisibility = () => {
      dispatch(toggleTrackVisibility({ trackId }));
    };

    const onRemove = () => {
      dispatch(removeTrack({ trackId })); // Remove from the slice.
    };

    return (
      <div
        className={classNames(styles.card, { [styles['card--unsuccessful']]: !hasTrackDataLoaded })}
      >
        {!noTrackData && !isError ? (
          <VdsIconButton
            icon={visible ? 'show' : 'hide'}
            size="large"
            variant="tertiary"
            onClick={onToggleVisibility}
            disabled={!isSuccess} // Disable the visibility toggle while waiting for the first data response.
          />
        ) : (
          <VdsIcon svgIcon="information" size="24px" color="red-500" />
        )}
        <div className={styles.details}>
          <div
            className={classNames(styles.name, {
              [styles['name--unsuccessful']]: !hasTrackDataLoaded,
            })}
          >
            {assetName}
          </div>
          <div className={styles.timeRange}>
            <span className={styles.timeRange__date}>{showDateOnlyFromIso(startTime)}</span>
            <span className={styles.timeRange__time}>
              {showTimeOnlyFromIso(startTime, true)} - {showTimeOnlyFromIso(endTime, true)}
            </span>
          </div>
        </div>
        <div className={styles.remove}>
          <VdsIconButton icon="close" size="medium" variant="tertiary" onClick={onRemove} />
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default AssetTrackCard;
